import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import QuizHeader from './components/QuizHeader';
import QuizNavigation from './components/QuizNavigation';
import QuizResults from './components/QuizResults';
import MultipleChoiceQuestion from './components/MultipleChoiceQuestion';
import RankingQuestion from './components/RankingQuestion';
import ScenarioQuestion from './components/ScenarioQuestion';
import VisualPreferenceQuestion from './components/VisualPreferenceQuestion';
import { emailNotificationService } from '../../services/emailNotificationService';
import { supabase } from '../../lib/supabase';

const InteractiveQuizSystem = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const fieldId = searchParams?.get('field_id');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [badgeToast, setBadgeToast] = useState(null);

  const shuffleOptions = (quiz) => {
    if (!quiz?.questions) return quiz;
    return {
      ...quiz,
      questions: quiz?.questions?.map((q) =>
        q?.type === 'multiple-choice'
          ? { ...q, options: [...q?.options]?.sort(() => Math.random() - 0.5) }
          : q
      )
    };
  };

  useEffect(() => {
    const loadQuizData = async () => {
      let selectedQuizId = 'aptitude-generale';
      let selectedQuizTitle = "Évaluation d'Aptitude Générale";
      let selectedCategory = 'Orientation Générale';
      let q1Text = "Quelle matière vous passionne le plus?";
      let q2Text = "Classez ces compétences selon votre niveau de maîtrise";

      if (fieldId) {
        const { data: field, error } = await supabase?.from('fields')?.select('field_data, name')?.eq('id', fieldId)?.single();

        if (!error && field?.field_data?.quiz) {
          setQuizData(shuffleOptions(field?.field_data?.quiz));
          setLoading(false);
          return;
        }

        if (!error && field?.interest) {
          const interestMap = {
            sciences: { id: 'aptitude-sciences', title: 'Évaluation Scientifique', category: 'Sciences', q1: 'Quelle branche scientifique vous attire le plus?', q2: 'Classez ces compétences scientifiques' },
            health: { id: 'aptitude-sante', title: 'Évaluation Santé', category: 'Santé', q1: 'Quel domaine de la santé vous intéresse?', q2: 'Classez ces compétences médicales' },
            business: { id: 'aptitude-business', title: 'Évaluation Business', category: 'Business', q1: 'Quel aspect du business vous passionne?', q2: 'Classez ces compétences entrepreneuriales' },
            humanities: { id: 'aptitude-humanities', title: 'Évaluation Humanités', category: 'Humanités', q1: 'Quel domaine des humanités vous attire?', q2: 'Classez ces compétences en sciences humaines' },
            arts: { id: 'aptitude-arts', title: 'Évaluation Arts', category: 'Arts', q1: 'Quelle forme d\'art vous passionne?', q2: 'Classez ces compétences artistiques' }
          };

          const mapped = interestMap?.[field?.interest];
          if (mapped) {
            selectedQuizId = mapped?.id;
            selectedQuizTitle = mapped?.title;
            selectedCategory = mapped?.category;
            q1Text = mapped?.q1;
            q2Text = mapped?.q2;
          }
        }
      }

      setQuizData(shuffleOptions({
        id: selectedQuizId,
        title: selectedQuizTitle,
        description: "Découvrez vos forces académiques et vos préférences d'apprentissage",
        timeEstimate: 15,
        category: selectedCategory,
        questions: [
        {
          id: "q1",
          type: "multiple-choice",
          text: q1Text,
          context: "Pensez à la matière où vous excellez naturellement et qui vous intéresse vraiment.",
          options: [
          { id: "opt1", text: "Mathématiques et Sciences", isCorrect: false, explanation: "Excellent choix pour les esprits analytiques et logiques." },
          { id: "opt2", text: "Langues et Littérature", isCorrect: false, explanation: "Parfait pour ceux qui aiment la communication et l'expression." },
          { id: "opt3", text: "Sciences Sociales et Histoire", isCorrect: false, explanation: "Idéal pour comprendre la société et les relations humaines." },
          { id: "opt4", text: "Arts et Créativité", isCorrect: false, explanation: "Excellent pour les esprits créatifs et innovants." }
          ]
        },
        {
          id: "q2",
          type: "ranking",
          text: q2Text,
          instruction: "Placez vos compétences les plus fortes en haut",
          items: [
          { id: "skill1", text: "Résolution de problèmes mathématiques" },
          { id: "skill2", text: "Expression écrite et orale" },
          { id: "skill3", text: "Analyse et pensée critique" },
          { id: "skill4", text: "Créativité et innovation" },
          { id: "skill5", text: "Travail d'équipe et collaboration" }
          ]
        },
        {
          id: "q3",
          type: "scenario",
          text: "Situation Professionnelle",
          scenario: "Vous êtes responsable d'un projet de groupe important avec une échéance serrée. Un membre de l'équipe ne contribue pas efficacement et cela affecte le moral du groupe. Les autres membres commencent à se plaindre et la qualité du travail diminue.",
          scenarioImage: "https://img.rocket.new/generatedImages/rocket_gen_img_16f4d4148-1770485658847.png",
          scenarioImageAlt: "Diverse group of young professionals collaborating around a modern office table with laptops and documents discussing project work",
          instruction: "Quelle serait votre approche dans cette situation?",
          options: [
          { id: "opt1", text: "Organiser une réunion d'équipe pour discuter ouvertement des problèmes", description: "Approche collaborative et transparente" },
          { id: "opt2", text: "Parler individuellement avec le membre en difficulté pour comprendre ses défis", description: "Approche empathique et personnalisée" },
          { id: "opt3", text: "Réorganiser les tâches pour maximiser les forces de chacun", description: "Approche pragmatique et orientée solutions" },
          { id: "opt4", text: "Informer le superviseur et demander des conseils", description: "Approche prudente et hiérarchique" }
          ]
        },
        {
          id: "q4",
          type: "visual-preference",
          text: "Quels environnements de travail vous attirent?",
          multiSelect: true,
          options: [
          { id: "env1", title: "Laboratoire de Recherche", description: "Environnement scientifique et analytique", image: "https://img.rocket.new/generatedImages/rocket_gen_img_17b84bf3b-1765284590370.png", imageAlt: "Modern scientific laboratory with researchers in white coats working with microscopes and advanced equipment under bright fluorescent lighting" },
          { id: "env2", title: "Bureau Créatif", description: "Espace de design et d'innovation", image: "https://img.rocket.new/generatedImages/rocket_gen_img_12e085d6c-1765477590528.png", imageAlt: "Bright creative office space with colorful furniture, open layout, collaborative workstations, and modern design elements with natural lighting" },
          { id: "env3", title: "Salle de Classe", description: "Environnement éducatif et pédagogique", image: "https://img.rocket.new/generatedImages/rocket_gen_img_17fb404f5-1767944739008.png", imageAlt: "Traditional classroom setting with rows of desks, blackboard, and educational materials creating a structured learning environment" }
          ]
        }
        ]
      }));
      setLoading(false);
    };

    loadQuizData();
  }, [fieldId]);

  const currentQuestion = quizData?.questions?.[currentQuestionIndex];

  const handleAnswer = (questionId, answer) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: answer
    }));
    setShowFeedback(true);
  };

  const handleNext = () => {
    if (currentQuestionIndex < quizData?.questions?.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setShowFeedback(false);
    } else {
      completeQuiz();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      setShowFeedback(false);
    }
  };

  const completeQuiz = () => {
    calculateResults();
  };

  const calculateResults = () => {
    let totalScore = 0;
    let maxScore = 0;

    // Category scores for strength analysis
    const categoryScores = {
      analytical: 0,
      creative: 0,
      social: 0,
      practical: 0,
      communication: 0
    };

    quizData?.questions?.forEach((question) => {
      const userAnswer = answers?.[question?.id];
      maxScore += 10;

      if (question?.type === 'multiple-choice') {
        const selectedOption = question?.options?.find((opt) => opt?.id === userAnswer);
        if (selectedOption?.isCorrect) {
          totalScore += 10;
        }
        // Analyze answer for category scoring
        if (userAnswer === 'opt1') {
          categoryScores.analytical += 10;
        } else if (userAnswer === 'opt2') {
          categoryScores.communication += 10;
        } else if (userAnswer === 'opt3') {
          categoryScores.social += 10;
        } else if (userAnswer === 'opt4') {
          categoryScores.creative += 10;
        }
      } else if (question?.type === 'ranking') {
        if (userAnswer && Array.isArray(userAnswer)) {
          totalScore += 7;
          // Analyze ranking for strengths
          if (userAnswer?.[0]?.id === 'skill1') categoryScores.analytical += 8;
          if (userAnswer?.[0]?.id === 'skill2') categoryScores.communication += 8;
          if (userAnswer?.[0]?.id === 'skill3') categoryScores.analytical += 8;
          if (userAnswer?.[0]?.id === 'skill4') categoryScores.creative += 8;
          if (userAnswer?.[0]?.id === 'skill5') categoryScores.social += 8;
        }
      } else if (question?.type === 'scenario') {
        if (userAnswer) {
          totalScore += 8;
          // Analyze scenario choice
          if (userAnswer === 'opt1') categoryScores.social += 8;
          if (userAnswer === 'opt2') categoryScores.communication += 8;
          if (userAnswer === 'opt3') categoryScores.practical += 8;
          if (userAnswer === 'opt4') categoryScores.social += 8;
        }
      } else if (question?.type === 'visual-preference') {
        if (userAnswer) {
          totalScore += 6;
          // Analyze visual preferences
          if (Array.isArray(userAnswer)) {
            if (userAnswer?.includes('env1')) categoryScores.analytical += 6;
            if (userAnswer?.includes('env2')) categoryScores.creative += 6;
            if (userAnswer?.includes('env3')) categoryScores.communication += 6;
          }
        }
      }
    });

    const percentageScore = Math.round(totalScore / maxScore * 100);
    const correctAnswers = Math.round(percentageScore / 100 * quizData?.questions?.length);
    let awardedPoints = percentageScore >= 60 ? 100 : 30;
    const pointsEarned = awardedPoints;

    // Generate strengths based on category scores
    const strengthsArray = Object.entries(categoryScores)?.map(([category, score]) => ({
      category,
      score: Math.min(100, Math.round(score / 40 * 100)), // Normalize to percentage
      rawScore: score
    }))?.sort((a, b) => b?.rawScore - a?.rawScore)?.slice(0, 4); // Top 4 strengths

    const strengths = strengthsArray?.map((item) => {
      const categoryInfo = {
        analytical: {
          area: 'Pensée Analytique',
          description: 'Excellente capacité à résoudre des problèmes complexes et à analyser des données'
        },
        creative: {
          area: 'Créativité et Innovation',
          description: 'Fort potentiel créatif et capacité à générer des idées originales'
        },
        social: {
          area: 'Intelligence Sociale',
          description: 'Compétences interpersonnelles développées et sens du travail d\'équipe'
        },
        practical: {
          area: 'Approche Pragmatique',
          description: 'Capacité à trouver des solutions pratiques et orientées résultats'
        },
        communication: {
          area: 'Communication',
          description: 'Excellentes compétences en expression écrite et orale'
        }
      };

      return {
        area: categoryInfo?.[item?.category]?.area || item?.category,
        score: item?.score,
        description: categoryInfo?.[item?.category]?.description || 'Compétence développée dans ce domaine'
      };
    });

    // Generate weaknesses (bottom 2 categories)
    const weaknesses = Object.entries(categoryScores)?.map(([category, score]) => ({
      category,
      score: Math.min(100, Math.round(score / 40 * 100)),
      rawScore: score
    }))?.sort((a, b) => a?.rawScore - b?.rawScore)?.slice(0, 2)?.map((item) => ({
      area: item?.category,
      score: item?.score,
      suggestion: `Développer davantage vos compétences en ${item?.category}`
    }));

    // Generate recommended fields based on strengths
    const recommendedFields = generateRecommendedFields(strengthsArray);

    // Determine badge earned
    let badgeEarned = null;
    if (percentageScore >= 90) {
      badgeEarned = 'Expert en Orientation';
    } else if (percentageScore >= 75) {
      badgeEarned = 'Explorateur Assidu';
    } else if (percentageScore >= 60) {
      badgeEarned = 'Apprenant Motivé';
    }

    // Determine validation status based on minimum passing score
    const validated = percentageScore >= 70;

    let pointsMessage = '';

    const results = {
      overallScore: percentageScore,
      score: percentageScore,
      totalQuestions: quizData?.questions?.length,
      correctAnswers: correctAnswers,
      incorrectAnswers: quizData?.questions?.length - correctAnswers,
      pointsEarned: pointsEarned,
      pointsMessage: pointsMessage,
      strengths: strengths,
      weaknesses: weaknesses,
      recommendedFields: recommendedFields,
      badgeEarned: validated ? badgeEarned : null,
      category: quizData?.category,
      recommendations: generateRecommendations(percentageScore),
      validated: validated
    };

    setQuizResults(results);
    setQuizCompleted(true);

    // Save quiz result and send email notification
    const saveAndNotify = async () => {
      // Save quiz result + send notification (non-blocking)
      try {
        if (isAuthenticated && user) {
          const saveResult = await emailNotificationService?.saveQuizResult({
            quiz_id: quizData?.id,
            quiz_title: quizData?.title,
            score: percentageScore,
            total_questions: quizData?.questions?.length,
            correct_answers: correctAnswers,
            answers: answers,
            field_id: fieldId || null
          });

          if (saveResult?.success) {
            await emailNotificationService?.sendQuizResultNotification(
              saveResult?.data?.user_id,
              {
                quiz_title: quizData?.title,
                score: percentageScore,
                total_questions: quizData?.questions?.length,
                correct_answers: correctAnswers
              }
            );
          }
        }
      } catch (error) {
        console.error('Error saving quiz result or sending notification:', error);
      }

      // Always update field_progress + award points when authenticated and fieldId present
      if (isAuthenticated && user && fieldId) {
        try {
          // Award points once
          console.log('[POINTS] START', {
            userId: user?.id,
            fieldId,
            percentageScore
          });
          const { data: progress, error: progressError } = await supabase?.from('field_progress')?.select('points_awarded, quiz_validated, last_points_awarded, validation_points_awarded')?.eq('user_id', user?.id)?.eq('field_id', fieldId)?.maybeSingle();

          console.log('[POINTS] PROGRESS_FETCH', {
            progress,
            progressError
          });

          if (progressError) {
            console.error('[field_progress] Error fetching field_progress:', progressError);
          }

          const corrupted = progress?.points_awarded === true && (!progress?.last_points_awarded || progress?.last_points_awarded === 0) && progress?.validation_points_awarded === false;
          const pointsAwarded = corrupted ? false : (progress?.points_awarded || false);
          const quizValidated = progress?.quiz_validated || false;
          let awardedPointsThisAttempt = 0;
          let shouldSetValidated = false;
          let shouldSetValidationPoints = false;

          if (percentageScore < 70) {
            if (!pointsAwarded) {
              awardedPointsThisAttempt = 30;
              shouldSetValidated = false;
              pointsMessage = 'Points d\'exploration gagnés : +30';
            } else {
              awardedPointsThisAttempt = 0;
              pointsMessage = 'Points déjà attribués pour cette filière : +0';
            }
          } else {
            // percentageScore >= 70
            if (!pointsAwarded) {
              awardedPointsThisAttempt = 100;
              shouldSetValidated = true;
              shouldSetValidationPoints = true;
              pointsMessage = 'Filière validée ! Points gagnés : +100';
            } else if (!quizValidated && !(progress?.validation_points_awarded)) {
              awardedPointsThisAttempt = 70;
              shouldSetValidated = true;
              shouldSetValidationPoints = true;
              pointsMessage = 'Amélioration ! Points de validation débloqués : +70';
            } else {
              awardedPointsThisAttempt = 0;
              pointsMessage = 'Points déjà attribués pour cette filière : +0';
            }
          }

          console.log('[POINTS] CALCULATION', {
            pointsAwarded,
            quizValidated,
            awardedPointsThisAttempt,
            shouldSetValidated
          });

          if (awardedPointsThisAttempt > 0) {
            const { data: profile, error: pErr } = await supabase?.from('user_profiles')?.select('points')?.eq('id', user?.id)?.single();

            console.log('[POINTS] PROFILE_FETCH', {
              profile,
              pErr
            });

            if (pErr) {
              console.error('[Points Update]', pErr);
            } else {
              const newPoints = (profile?.points || 0) + awardedPointsThisAttempt;

              const { data: updated, error: uErr } = await supabase?.from('user_profiles')?.update({ points: newPoints })?.eq('id', user?.id)?.select('points')?.single();

              console.log('[POINTS] PROFILE_UPDATE', {
                updated,
                uErr,
                newPoints
              });

              if (uErr || !updated) {
                console.error('[Points Update]', uErr);
                // Still mark quiz completed even if points update failed
                await supabase?.from('field_progress')?.upsert({
                  user_id: user?.id,
                  field_id: fieldId,
                  quiz_completed: true,
                  completed_at: new Date()?.toISOString()
                }, { onConflict: 'user_id,field_id' });
              } else {
                const updatePayload = {
                  quiz_completed: true,
                  completed_at: new Date()?.toISOString(),
                  ...(shouldSetValidated ? { quiz_validated: true } : {}),
                  ...(awardedPointsThisAttempt > 0 ? { points_awarded: true, last_points_awarded: awardedPointsThisAttempt } : {}),
                  ...(shouldSetValidationPoints ? { validation_points_awarded: true } : {})
                };

                const { error: updateProgressError } = await supabase?.from('field_progress')?.upsert({
                  user_id: user?.id,
                  field_id: fieldId,
                  ...updatePayload
                }, { onConflict: 'user_id,field_id' });

                console.log('[POINTS] PROGRESS_UPDATE', {
                  updatePayload,
                  updateProgressError
                });

                if (updateProgressError) {
                  console.error('[field_progress] Error updating field_progress after points award:', updateProgressError);
                } else {
                  results.pointsMessage = pointsMessage;
                  results.pointsEarned = awardedPointsThisAttempt;
                }
              }
            }
          } else {
            // No points to award — still mark quiz completed
            await supabase?.from('field_progress')?.upsert({
              user_id: user?.id,
              field_id: fieldId,
              quiz_completed: true,
              completed_at: new Date()?.toISOString()
            }, { onConflict: 'user_id,field_id' });
            results.pointsMessage = pointsMessage;
            results.pointsEarned = 0;
          }

          // Award field-specific badge when quiz becomes validated
          if (shouldSetValidated && fieldId) {
            const { data: field, error: fieldError } = await supabase?.from('fields')?.select('field_id, name, interest')?.eq('id', fieldId)?.single();

            if (!fieldError && field) {
              const iconMap = {
                sciences: '💻',
                health: '🩺',
                business: '📈',
                arts: '🎨',
                humanities: '📚'
              };

              const fieldBadge = {
                user_id: user?.id,
                achievement_id: `field_validated_${field?.field_id}`,
                achievement_title: `Filière validée : ${field?.name}`,
                achievement_description: `Vous avez validé la filière ${field?.name}.`,
                category: 'milestone',
                points: 0,
                icon: iconMap?.[field?.interest] || '🏅',
                earned_at: new Date()?.toISOString()
              };

              await supabase?.from('achievements')?.upsert(fieldBadge, { onConflict: 'user_id,achievement_id', ignoreDuplicates: false });
            }
          }

          // Award milestone badges if score >= 70
          if (percentageScore >= 70) {
            const { count } = await supabase?.from('field_progress')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('quiz_validated', true);

            const milestones = [];
            if (count >= 1) milestones?.push({ user_id: user?.id, achievement_id: 'explorer_lvl_1', achievement_title: 'Explorateur - Niveau 1', achievement_description: 'Vous avez validé 1 filière.', category: 'milestone', points: 0, icon: '🏅', earned_at: new Date()?.toISOString() });
            if (count >= 3) milestones?.push({ user_id: user?.id, achievement_id: 'explorer', achievement_title: 'Explorateur', achievement_description: 'Vous avez validé 3 filières.', category: 'milestone', points: 0, icon: '🏅', earned_at: new Date()?.toISOString() });
            if (count >= 5) milestones?.push({ user_id: user?.id, achievement_id: 'ready_for_orientation', achievement_title: 'Prêt pour l\'Orientation', achievement_description: 'Vous avez validé 5 filières.', category: 'milestone', points: 0, icon: '🏅', earned_at: new Date()?.toISOString() });

            if (milestones?.length > 0) {
              const { data: inserted, error: upsertError } = await supabase?.from('achievements')?.upsert(milestones, { onConflict: 'user_id,achievement_id', ignoreDuplicates: false })?.select();

              if (!upsertError && inserted && inserted?.length > 0) {
                const newBadge = inserted?.[0];
                setBadgeToast(newBadge?.achievement_title);
                setTimeout(() => setBadgeToast(null), 3000);
              }
            }
          }
        } catch (error) {
          console.error('Error updating field_progress or awarding points:', error);
        }
      }
    };

    saveAndNotify();
  };

  const generateRecommendations = (score) => {
    if (score >= 80) {
      return [
      "Excellent performance! You have strong aptitude in this area.",
      "Consider pursuing advanced studies in your areas of strength.",
      "You may be ready for more challenging academic programs."];

    } else if (score >= 60) {
      return [
      "Good performance with room for improvement.",
      "Focus on strengthening your weaker areas.",
      "Consider additional practice in specific subjects."];

    } else {
      return [
      "There's potential for growth in these areas.",
      "Consider additional support or tutoring.",
      "Practice regularly to build confidence and skills."];

    }
  };

  const generateRecommendedFields = (strengthsArray) => {
    // Map strengths to field recommendations
    const fieldMappings = {
      analytical: [
      { name: 'Ingénierie et Technologies', matchScore: 92 },
      { name: 'Sciences et Mathématiques', matchScore: 88 },
      { name: 'Informatique et Data Science', matchScore: 85 }],

      creative: [
      { name: 'Design et Arts Visuels', matchScore: 90 },
      { name: 'Architecture', matchScore: 85 },
      { name: 'Communication et Médias', matchScore: 82 }],

      social: [
      { name: 'Sciences Sociales et Psychologie', matchScore: 88 },
      { name: 'Ressources Humaines', matchScore: 85 },
      { name: 'Travail Social et Éducation', matchScore: 83 }],

      practical: [
      { name: 'Gestion de Projet', matchScore: 87 },
      { name: 'Administration des Affaires', matchScore: 84 },
      { name: 'Logistique et Supply Chain', matchScore: 80 }],

      communication: [
      { name: 'Journalisme et Communication', matchScore: 90 },
      { name: 'Relations Publiques', matchScore: 86 },
      { name: 'Langues et Traduction', matchScore: 83 }]

    };

    // Get top 3 recommended fields based on strongest categories
    const recommendations = [];
    const usedFields = new Set();

    for (const strength of strengthsArray) {
      const fields = fieldMappings?.[strength?.category] || [];
      for (const field of fields) {
        if (!usedFields?.has(field?.name) && recommendations?.length < 3) {
          recommendations?.push(field);
          usedFields?.add(field?.name);
        }
      }
      if (recommendations?.length >= 3) break;
    }

    // Fallback if not enough recommendations
    if (recommendations?.length < 3) {
      const fallbackFields = [
      { name: 'Sciences Générales', matchScore: 75 },
      { name: 'Commerce et Économie', matchScore: 72 },
      { name: 'Études Multidisciplinaires', matchScore: 70 }];


      for (const field of fallbackFields) {
        if (!usedFields?.has(field?.name) && recommendations?.length < 3) {
          recommendations?.push(field);
          usedFields?.add(field?.name);
        }
      }
    }

    return recommendations;
  };

  const handleRetake = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowFeedback(false);
    setQuizCompleted(false);
    setQuizResults(null);
  };

  const handleViewRecommendations = () => {
    navigate('/study-field-results');
  };

  const handleExit = () => {
    navigate('/student-dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du quiz...</p>
        </div>
      </div>
    );

  }

  if (!quizData) {
    return null;
  }

  if (quizCompleted && quizResults) {
    return (
      <QuizResults
        results={quizResults}
        onRetake={handleRetake}
        onViewRecommendations={handleViewRecommendations}
      />
    );


  }

  const renderQuestion = () => {
    switch (currentQuestion?.type) {
      case 'multiple-choice':
        return (
          <MultipleChoiceQuestion
            question={currentQuestion}
            selectedAnswer={answers?.[currentQuestion?.id]}
            onAnswerSelect={(answer) => handleAnswer(currentQuestion?.id, answer)}
            showFeedback={showFeedback}
            isCorrect={currentQuestion?.options?.find((opt) => opt?.id === answers?.[currentQuestion?.id])?.isCorrect || false}
          />
        );


      case 'ranking':
        return (
          <RankingQuestion
            question={currentQuestion}
            rankedItems={answers?.[currentQuestion?.id] || []}
            onRankingChange={(ranking) => handleAnswer(currentQuestion?.id, ranking)}
          />
        );


      case 'scenario':
        return (
          <ScenarioQuestion
            question={currentQuestion}
            selectedAnswer={answers?.[currentQuestion?.id]}
            onAnswerSelect={(answer) => handleAnswer(currentQuestion?.id, answer)}
            showFeedback={showFeedback}
          />
        );


      case 'visual-preference':
        return (
          <VisualPreferenceQuestion
            question={currentQuestion}
            selectedAnswers={answers?.[currentQuestion?.id] || []}
            onAnswerSelect={(selection) => handleAnswer(currentQuestion?.id, selection)}
            multiSelect={currentQuestion?.multiSelect || false}
          />
        );


      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <QuizHeader
        quizTitle={quizData?.title}
        currentQuestion={currentQuestionIndex + 1}
        totalQuestions={quizData?.questions?.length}
        timeEstimate={quizData?.timeEstimate}
        onExit={handleExit} />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {renderQuestion()}
        </div>

        <QuizNavigation
          currentQuestion={currentQuestionIndex + 1}
          totalQuestions={quizData?.questions?.length}
          canGoNext={!!answers?.[currentQuestion?.id]}
          canGoPrevious={currentQuestionIndex > 0}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSubmit={completeQuiz}
          showFeedback={showFeedback}
          onContinue={handleNext}
          isLastQuestion={currentQuestionIndex === quizData?.questions?.length - 1} />
        
      </div>
      
      {badgeToast && (
        <div className="fixed top-4 right-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-bounce">
          🏅 Badge débloqué : {badgeToast}
        </div>
      )}
    </div>
  );

};

export default InteractiveQuizSystem;